<script setup lang="ts">
import type { WeeklySchedule, DayOfWeek } from '@/types'
import { formatScheduleGrouped, isCurrentlyOpen } from '@/types'

const props = defineProps<{
  hours: WeeklySchedule
  emergencyHours?: WeeklySchedule | null
  emergencyFee?: string | null
  holidayHours?: string | null
  emergencyHolidayHours?: string | null
  compact?: boolean
}>()

const groupedHours = computed(() => {
  return formatScheduleGrouped(props.hours)
})

const groupedEmergencyHours = computed(() => {
  if (!props.emergencyHours) return null
  return formatScheduleGrouped(props.emergencyHours)
})

const currentDayKey = ref<DayOfWeek | null>(null)
const isMounted = ref(false)

onMounted(() => {
  const days: DayOfWeek[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
  currentDayKey.value = days[new Date().getDay()] || null
  isMounted.value = true
})

const currentDayHours = computed(() => {
  if (!currentDayKey.value) return null
  const dayHours = props.hours[currentDayKey.value]
  if (!dayHours) return null

  if (dayHours.status === 'closed') return 'Zavřeno'
  if (dayHours.status === '24h') return 'Nonstop'

  return dayHours.intervals.map((i) => `${i.open}-${i.close}`).join(', ')
})

// Determine which section is currently active (client-only)
const isRegularActive = computed(() => {
  if (!isMounted.value) return null
  return isCurrentlyOpen(props.hours) === true
})

const isEmergencyActive = computed(() => {
  if (!isMounted.value || !props.emergencyHours) return null
  if (isRegularActive.value) return false
  return isCurrentlyOpen(props.emergencyHours) === true
})
</script>

<template>
  <div>
    <!-- Hours list -->
    <div v-if="compact" class="text-sm text-muted">
      <ClientOnly>
        <template v-if="currentDayHours">
          Dnes: {{ currentDayHours }}
        </template>
        <template #fallback>
          <span class="text-dimmed">...</span>
        </template>
      </ClientOnly>
    </div>

    <div v-else class="space-y-1">
      <!-- Regular hours -->
      <div
        class="space-y-1 transition-opacity duration-300"
        :class="{ 'opacity-40': isEmergencyActive }"
      >
        <div
          v-for="{ days, hours, dayKeys } in groupedHours"
          :key="days"
          class="flex justify-between text-sm rounded-md px-2 py-0.5 -mx-2"
          :class="isMounted && currentDayKey && dayKeys.includes(currentDayKey) ? 'bg-primary/10 text-highlighted font-medium' : 'text-muted'"
        >
          <span>{{ days }}</span>
          <span>{{ hours }}</span>
        </div>
        <div v-if="holidayHours" class="flex justify-between text-sm text-muted">
          <span>Svátky</span>
          <span>{{ holidayHours }}</span>
        </div>
      </div>

      <!-- Emergency hours -->
      <template v-if="groupedEmergencyHours && groupedEmergencyHours.length > 0">
        <USeparator class="my-2" />
        <div
          class="space-y-1 transition-opacity duration-300"
          :class="{ 'opacity-40': isRegularActive }"
        >
          <div class="flex items-center gap-1.5 text-sm font-medium text-red-700 dark:text-red-400">
            <UIcon name="i-lucide-siren" class="w-4 h-4" />
            Pohotovost
          </div>
          <div
            v-for="{ days, hours, dayKeys } in groupedEmergencyHours"
            :key="days"
            class="flex justify-between text-sm rounded-md px-2 py-0.5 -mx-2"
            :class="isMounted && currentDayKey && dayKeys.includes(currentDayKey) ? 'bg-red-500/10 text-red-700 dark:text-red-300 font-medium' : 'text-red-600/60 dark:text-red-400/60'"
          >
            <span>{{ days }}</span>
            <span>{{ hours }}</span>
          </div>
          <div v-if="emergencyHolidayHours" class="flex justify-between text-sm text-red-600 dark:text-red-400">
            <span>Svátky</span>
            <span>{{ emergencyHolidayHours }}</span>
          </div>
          <UAlert
            v-if="emergencyFee"
            color="error"
            variant="subtle"
            icon="i-lucide-banknote"
            :title="`Poplatek: ${emergencyFee}`"
            class="mt-2"
          />
        </div>
      </template>
    </div>
  </div>
</template>
